import { action, atom, onCtxAbort, reaction } from '@reatom/framework'

import { Array, Iterable, pipe } from '@blade/std-lib'
import { delugeIdAtom } from '@root/entities/auth'
import { selectedNetworkAtom } from '@root/entities/network'
import { TrackingWallet } from '@root/entities/wallet-tracking'
import { reatomRequest } from '@root/shared/lib'

import { walletTrackingAddedEvent } from './reatom-add-wallet-tracker'
import { walletTrackingDeletedEvent } from './reatom-delete-wallet-tracking'
import { getUserTrackingWalletsRequestAction } from './wallet-tracker-requests'

export const trackingWalletsStateAtom = reatomRequest({
  execAction: getUserTrackingWalletsRequestAction,
  mapOutput: (_, wallets) =>
    pipe(wallets, Array.sort(TrackingWallet.cmpCreatedAtDesc)),
  // eslint-disable-next-line @reatom/unit-naming-rule
  name: 'tracking-wallets-state',
})

export const trackingWalletsAtom = atom((ctx) => {
  return ctx.spy(trackingWalletsStateAtom).value ?? []
}, 'trackingWalletsAtom')

export const trackingWalletsByAddressAtom = atom((ctx) => {
  return pipe(
    ctx.spy(trackingWalletsAtom),
    Iterable.toRecord((it) => it.walletAddress),
  )
}, 'trackingWalletsByAddressAtom')

/**
 * Fetches the tracked list if nobody has yet.
 *
 * `reatomRequest` loads on `execute` and not on connect, so subscribing to
 * the list is not the same as asking for it. The reaction below does ask —
 * but only where it runs, and on a pair page it does not: measured on
 * staging, `get-tracking-wallets` was requested zero times there while the
 * chart sat waiting for the names it needs to mark a wallet's trades.
 *
 * Anything that needs the list outside the tracker itself calls this. It is
 * idempotent: a second caller with a list already in hand does nothing.
 */
export const ensureTrackingWalletsAction = action((ctx) => {
  if (ctx.get(trackingWalletsAtom).length > 0) {
    return
  }
  const delugeId = ctx.get(delugeIdAtom)
  if (!delugeId) {
    return
  }
  trackingWalletsStateAtom.execute(ctx, {
    delugeId,
    network: ctx.get(selectedNetworkAtom),
  })
}, 'ensureTrackingWalletsAction')

export const loadTrackingWalletsReaction = reaction((ctx) => {
  const delugeId = ctx.spy(delugeIdAtom)
  if (!delugeId) {
    return
  }
  const network = ctx.spy(selectedNetworkAtom)
  trackingWalletsStateAtom.execute(ctx, { delugeId, network })

  const unsubscribeDeleted = walletTrackingDeletedEvent.onCall(
    (ctx, { walletAddress: _ }) => {
      trackingWalletsStateAtom.refresh(ctx, { strategy: 'success-only' })
    },
  )
  const unsubscribeAdded = walletTrackingAddedEvent.onCall((ctx) => {
    trackingWalletsStateAtom.refresh(ctx, { strategy: 'success-only' })
  })

  onCtxAbort(ctx, () => {
    unsubscribeDeleted()
    unsubscribeAdded()
  })
}, 'loadTrackingWalletsReaction')
